import { ConnectedErrorReset } from 'components/common-form/sidePanel/actions/ErrorReset';
import { HalfQuoteActions } from 'components/common-form/sidePanel/actions/quote/HalfQuoteActionButtons';
import { CancelQuoteButtonConnected } from 'components/common-form/sidePanel/actions/shared/CancelButton';
import { getTradeWayIntlKey } from 'components/shared/PriceNext/TradeWayLiteral.helper';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { RequestType } from 'models/productsReferential';
import { TradeWay } from 'models/TradeWay';
import type { ReactNode } from 'react';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { getFormQuoteResult, getSidePanelAddonActionOrDefault } from 'state/selectors';
import type { ActionButtonsType } from 'state/selectors/meta/quote/utils';
import { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { QuoteActions } from './quote/QuoteActions';
import { ConfirmTradeButtonsComponentNext, InitiateTradeButtonsComponentNext } from './trade';
import { TradeDoneButton } from './trade/tradeDone';

interface ActionButtonsProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;
  quoteId?: string;
  type: ActionButtonsType;
  tradeWay: TradeWay;
  children?: ReactNode;
  requestType?: RequestType;
}

export const ActionButtons: FC<ActionButtonsProps> = ({
  tabId,
  quoteId,
  workspaceType,
  type,
  tradeWay,
  children,
  requestType,
}) => {
  const isInternalUser = useIsInternalUser();
  const isRequestForStream = requestType === 'RequestForStream';

  const addonAction = useSelector((state: AppState) => getSidePanelAddonActionOrDefault(state, tabId, workspaceType));
  const isExecOnlyAddon = useSelector(
    (state: AppState) => getFormQuoteResult(state, workspaceType, tabId)?.isExecOnlyAddon,
  );

  switch (type) {
    case 'ErrorReset':
      return (
        <>
          <ConnectedErrorReset formId={tabId} workspace={workspaceType} />
          {(workspaceType === 'static' || workspaceType === 'dynamic') && (
            <QuoteActions tabId={tabId} workspaceType={workspaceType}>
              {children}
            </QuoteActions>
          )}
        </>
      );

    case 'Quote':
      return (
        <QuoteActions tabId={tabId} workspaceType={workspaceType}>
          {children}
        </QuoteActions>
      );
    case 'QuoteAndCancel':
      return (
        <HalfQuoteActions formId={tabId} workspace={workspaceType}>
          {children}
          {!isRequestForStream && (
            <CancelQuoteButtonConnected className="my-1 px-1 w-50" formId={tabId} workspace={workspaceType} />
          )}
        </HalfQuoteActions>
      );
    case 'InitiateTrade':
      return (
        <InitiateTradeButtonsComponentNext
          tabId={tabId}
          workspaceType={workspaceType}
          id="btn-trade"
          tradeLabelKey={getTradeWayIntlKey(tradeWay, isInternalUser, addonAction, isExecOnlyAddon)}
          e2e="execute-trade"
        >
          {children}
        </InitiateTradeButtonsComponentNext>
      );
    case 'ConfirmTrade':
      return (
        <ConfirmTradeButtonsComponentNext
          tabId={tabId}
          workspaceType={workspaceType}
          id="btn-confirm-execute-trade"
          tradeLabelKey={getTradeWayIntlKey(tradeWay, isInternalUser, addonAction, isExecOnlyAddon, true)}
          e2e="confirm-trade"
        >
          {children}
        </ConfirmTradeButtonsComponentNext>
      );
    case 'TradeDone':
      return <TradeDoneButton tabId={tabId} quoteId={quoteId!} workspaceType={workspaceType} className="w-100" />;
    case 'None':
      return null;
  }
};

ActionButtons.displayName = 'ActionButtons';
